"use client";
import React, { useState } from "react";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import {
  FaExclamationCircle,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";

export default function News2Calculator() {
  const [form, setForm] = useState({
    rr: "",
    spo2: "",
    oxygen: false,
    temp: "",
    sbp: "",
    hr: "",
    avpu: "A",
  });

  const [score, setScore] = useState(null);
  const [obsScores, setObsScores] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const calculateNEWS2 = () => {
    let s = 0;
    const obs = { rr: 0, spo2: 0, oxygen: 0, temp: 0, sbp: 0, hr: 0, avpu: 0 };

    const rr = Number(form.rr);
    if (rr <= 8) obs.rr = 3;
    else if (rr <= 11) obs.rr = 1;
    else if (rr <= 20) obs.rr = 0;
    else if (rr <= 24) obs.rr = 2;
    else if (rr >= 25) obs.rr = 3;
    s += obs.rr;

    const spo2 = Number(form.spo2);
    if (spo2 <= 91) obs.spo2 = 3;
    else if (spo2 <= 93) obs.spo2 = 2;
    else if (spo2 <= 95) obs.spo2 = 1;
    else if (spo2 >= 96) obs.spo2 = 0;
    s += obs.spo2;

    if (form.oxygen) {
      obs.oxygen = 2;
      s += 2;
    }

    const temp = Number(form.temp);
    if (temp <= 35.0) obs.temp = 3;
    else if (temp <= 36.0) obs.temp = 1;
    else if (temp <= 38.0) obs.temp = 0;
    else if (temp <= 39.0) obs.temp = 1;
    else if (temp >= 39.1) obs.temp = 2;
    s += obs.temp;

    const sbp = Number(form.sbp);
    if (sbp <= 90) obs.sbp = 3;
    else if (sbp <= 100) obs.sbp = 2;
    else if (sbp <= 110) obs.sbp = 1;
    else if (sbp <= 219) obs.sbp = 0;
    else if (sbp >= 220) obs.sbp = 3;
    s += obs.sbp;

    const hr = Number(form.hr);
    if (hr <= 40) obs.hr = 3;
    else if (hr <= 50) obs.hr = 1;
    else if (hr <= 90) obs.hr = 0;
    else if (hr <= 110) obs.hr = 1;
    else if (hr <= 130) obs.hr = 2;
    else if (hr >= 131) obs.hr = 3;
    s += obs.hr;

    if (form.avpu !== "A") {
      obs.avpu = 3;
      s += 3;
    }

    setScore(s);
    setObsScores(obs);
  };

  const resetForm = () => {
    setForm({
      rr: "",
      spo2: "",
      oxygen: false,
      temp: "",
      sbp: "",
      hr: "",
      avpu: "A",
    });
    setScore(null);
    setObsScores(null);
  };

  const colourForScore = (v) => {
    if (v === 1) return "#FFEB3B";
    if (v === 2) return "#FF9800";
    if (v === 3) return "#D32F2F";
    return "#FFFFFF";
  };

  const riskBadge = (vital, value) => {
    if (value === 3)
      return (
        <FaExclamationCircle style={{ color: "#D32F2F", marginLeft: "6px" }} />
      );
    if (value === 2)
      return (
        <FaExclamationTriangle
          style={{ color: "#FF9800", marginLeft: "6px" }}
        />
      );
    if (value === 0)
      return <FaCheckCircle style={{ color: "#007F3B", marginLeft: "6px" }} />;
    return null;
  };

  return (
    <>
      <Header />

      <style>{`
        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
        .blink {
          animation: blink 1s infinite;
        }

        .news-table-container {
          overflow-x: auto;
          width: 100%;
        }

        @media (max-width: 600px) {
          .nhsuk-table th, .nhsuk-table td {
            padding: 10px 8px;
            font-size: 0.95rem;
          }
        }
      `}</style>

      <main
        className="nhsuk-width-container nhsuk-u-padding-top-6 nhsuk-u-padding-bottom-6"
        style={{ fontFamily: "Frutiger" }}
      >
        <div className="nhsuk-grid-row">
          <div className="nhsuk-grid-column-two-thirds">
            <h1 className="nhsuk-heading-xl nhsuk-u-margin-bottom-4">
              National Early Warning Score 2 (NEWS2)
            </h1>

            <div className="nhsuk-card nhsuk-u-margin-bottom-5">
              <div className="nhsuk-card__content">
                <h2 className="nhsuk-card__heading nhsuk-heading-l">
                  Enter observations
                </h2>

                {/* INPUT FIELDS (unchanged) */}
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-heading-xs" htmlFor="rr">
                    Respiratory rate
                  </label>
                  <input
                    className="nhsuk-input nhsuk-u-width-one-quarter"
                    id="rr"
                    name="rr"
                    type="number"
                    placeholder="e.g., 16"
                    min="0"
                    max="60"
                    step="1"
                    value={form.rr}
                    onChange={handleChange}
                  />
                  <span className="nhsuk-hint">breaths per minute</span>
                </div>

                <div className="nhsuk-form-group">
                  <label
                    className="nhsuk-label nhsuk-heading-xs"
                    htmlFor="spo2"
                  >
                    Oxygen saturation (SpO₂)
                  </label>
                  <input
                    className="nhsuk-input nhsuk-u-width-one-quarter"
                    id="spo2"
                    name="spo2"
                    type="number"
                    placeholder="e.g., 98"
                    min="0"
                    max="100"
                    step="1"
                    value={form.spo2}
                    onChange={handleChange}
                  />
                  <span className="nhsuk-hint">%</span>
                </div>

                <div className="nhsuk-form-group">
                  <div className="nhsuk-checkboxes__item">
                    <input
                      className="nhsuk-checkboxes__input"
                      type="checkbox"
                      id="oxygen"
                      name="oxygen"
                      checked={form.oxygen}
                      onChange={handleChange}
                    />
                    <label
                      className="nhsuk-checkboxes__label nhsuk-heading-xs"
                      htmlFor="oxygen"
                    >
                      Supplemental oxygen
                    </label>
                  </div>
                </div>

                <div className="nhsuk-form-group">
                  <label
                    className="nhsuk-label nhsuk-heading-xs"
                    htmlFor="temp"
                  >
                    Temperature
                  </label>
                  <input
                    className="nhsuk-input nhsuk-u-width-one-quarter"
                    id="temp"
                    name="temp"
                    type="number"
                    placeholder="e.g., 36.7"
                    min="30"
                    max="42"
                    step="0.1"
                    value={form.temp}
                    onChange={handleChange}
                  />
                  <span className="nhsuk-hint">°C</span>
                </div>

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-heading-xs" htmlFor="sbp">
                    Systolic blood pressure
                  </label>
                  <input
                    className="nhsuk-input nhsuk-u-width-one-quarter"
                    id="sbp"
                    name="sbp"
                    type="number"
                    placeholder="e.g., 128"
                    min="0"
                    max="300"
                    step="1"
                    value={form.sbp}
                    onChange={handleChange}
                  />
                  <span className="nhsuk-hint">mmHg</span>
                </div>

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-heading-xs" htmlFor="hr">
                    Heart rate
                  </label>
                  <input
                    className="nhsuk-input nhsuk-u-width-one-quarter"
                    id="hr"
                    name="hr"
                    type="number"
                    placeholder="e.g., 72"
                    min="0"
                    max="220"
                    step="1"
                    value={form.hr}
                    onChange={handleChange}
                  />
                  <span className="nhsuk-hint">beats/min</span>
                </div>

                <div className="nhsuk-form-group">
                  <label
                    className="nhsuk-label nhsuk-heading-xs"
                    htmlFor="avpu"
                  >
                    Consciousness
                  </label>
                  <select
                    className="nhsuk-select nhsuk-u-width-one-quarter"
                    id="avpu"
                    name="avpu"
                    value={form.avpu}
                    onChange={handleChange}
                  >
                    <option value="A">Alert</option>
                    <option value="V">Responds to Voice</option>
                    <option value="P">Responds to Pain</option>
                    <option value="U">Unresponsive</option>
                  </select>
                  <span className="nhsuk-hint">AVPU</span>
                </div>

                <div className="nhsuk-u-margin-top-4">
                  <button
                    className="nhsuk-button btn-nhs"
                    onClick={calculateNEWS2}
                  >
                    Calculate score
                  </button>
                  <button
                    className="nhsuk-button nhsuk-button--danger nhsuk-u-margin-left-2"
                    onClick={resetForm}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* UPDATED RESULTS SECTION */}
            {obsScores && (
              <div className="nhsuk-card nhsuk-u-margin-top-5">
                <div className="nhsuk-card__content">
                  <h2 className="nhsuk-heading-l">Results</h2>
                  <p className="nhsuk-body-m nhsuk-u-margin-bottom-3">
                    Total score: <strong>{score}</strong>
                  </p>

                  <div className="news-table-container">
                    <table className="nhsuk-table">
                      <thead className="nhsuk-table__head">
                        <tr>
                          <th>Type</th>
                          <th>Value</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody className="nhsuk-table__body">
                        <tr>
                          <td>Respiratory Rate</td>
                          <td>{form.rr || "—"} breaths/min</td>
                          <td>
                            <span className={obsScores.rr === 3 ? "blink" : ""}>
                              {obsScores.rr} {riskBadge("rr", obsScores.rr)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Oxygen Saturation</td>
                          <td>{form.spo2 || "—"}%</td>
                          <td>
                            <span
                              className={obsScores.spo2 === 3 ? "blink" : ""}
                            >
                              {obsScores.spo2}{" "}
                              {riskBadge("spo2", obsScores.spo2)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Supplemental Oxygen</td>
                          <td>{form.oxygen ? "Yes" : "No"}</td>
                          <td>
                            <span>
                              {form.oxygen ? 2 : 0}{" "}
                              {riskBadge("oxygen", form.oxygen ? 2 : 0)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Temperature</td>
                          <td>{form.temp || "—"} °C</td>
                          <td>
                            <span
                              className={obsScores.temp === 3 ? "blink" : ""}
                            >
                              {obsScores.temp}{" "}
                              {riskBadge("temp", obsScores.temp)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Systolic BP</td>
                          <td>{form.sbp || "—"} mmHg</td>
                          <td>
                            <span
                              className={obsScores.sbp === 3 ? "blink" : ""}
                            >
                              {obsScores.sbp} {riskBadge("sbp", obsScores.sbp)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Heart Rate</td>
                          <td>{form.hr || "—"} beats/min</td>
                          <td>
                            <span className={obsScores.hr === 3 ? "blink" : ""}>
                              {obsScores.hr} {riskBadge("hr", obsScores.hr)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>Consciousness</td>
                          <td>{form.avpu}</td>
                          <td>
                            <span
                              className={obsScores.avpu === 3 ? "blink" : ""}
                            >
                              {form.avpu === "A" ? 0 : 3}{" "}
                              {riskBadge("avpu", form.avpu === "A" ? 0 : 3)}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
